import Locale from '../utils/Locale';

export class State {
    id?: number;
    siteId: number;
    /** UUID of the entity the state item belongs to. */
    item: string;
    /** E.g: AutoFilterVersion, PowerState etc. */
    name: string; // todo: use `StateNames`
    /** The actual value of the state item. */
    state: string;
    /** ISO timestamp. Note: Currently we don't convert the incoming date string sent by the API from string to Date */
    updated: string;
    device?: string;
    /** Hint text, where the item was generated/overridden if it was. */
    mockSource?: string;
}

export enum siteMode {
    INSTALLER = "INSTALLER",
    NORMAL = "NORMAL",
    SERVICE = "SERVICE",
    FACTORY = "FACTORY"
}

export type PlatformCapabilities = {
    Controllers: PlatformCapabilitiesControllers[]
}
export type PlatformCapabilitiesControllers = {
    ControllerUUID: string;
    Modules: PlatformCapabilitiesModules[]
}
export type PlatformCapabilitiesModules = {
    Attached: boolean,
    ModuleNumber: string, // E.g: "1", or for a light river: "91-42-11-A0-3B"
    PortTypes: {
        Actuator?: PlatformCapabilitiesPorts[],
        Bluetooth?: PlatformCapabilitiesPorts[],
        Bus?: PlatformCapabilitiesPorts[],
        ChemistryORP?: PlatformCapabilitiesPorts[],
        ChemistryPH?: PlatformCapabilitiesPorts[],
        FlowSwitch?: PlatformCapabilitiesPorts[],
        HighVoltage?: PlatformCapabilitiesPorts[],
        InterfaceButtons?: PlatformCapabilitiesPorts[],
        LightDriverLowVoltage?: PlatformCapabilitiesPorts[],
        LowVoltage?: PlatformCapabilitiesPorts[],
        Ma?: PlatformCapabilitiesPorts[],
        Thermistor?: PlatformCapabilitiesPorts[],
    },
    ModuleData: {
        // Poolside Attendant
        BoardRevision: string, // E.g: 2,
        ModuleNumber: number,
        McuVersion: string, // E.g: "022"
        Serial: string,
    } | null,
    ModulePortInformation: {
        // Hayward HUA Light Driver
        Bus: number,
        BusClass: string, // E.g: "HaywardHUABus"
        AddressUniqueType: string, // E.g: "All"
    } | null,
    Type: string, // E.g: "POOLSIDE", "HAYWARD_HUA_LIGHT_DRIVER"
    UniqueBusIdentifier: string | null, // E.g: "91-42-11-A0-3B"
}
export type PlatformCapabilitiesPorts = {
    /** Note: Bluetooth does not use PortNumber, marking prop as optional */
    PortNumber?: number
}
export enum PlatformCapabilitiesModulesPortTypes {
    Actuator = "Actuator",
    Bus = "Bus",
    ChemistryORP = "ChemistryORP",
    ChemistryPH = "ChemistryPH",
    FlowSwitch = "FlowSwitch",
    HighVoltage = "HighVoltage",
    InterfaceButtons = "InterfaceButtons",
    LightDriverLowVoltage = "LightDriverLowVoltage",
    LowVoltage = "LowVoltage",
    Ma = "Ma",
    Thermistor = "Thermistor",
    Bluetooth = "Bluetooth",
}

export type ModeExitStrategy = {
    /** Enum with possible options as “ANYONE, SAME_USER, LOCAL_ONLY” */
    modeExitStrategy: exitModeStrategies,
    /** User UUID that placed the system into the mode */
    modeEnteredByUser: string,
    /** Dateteime when mode was entered */
    modeEnterTime: Date,
}

export enum exitModeStrategies {
    /** This is similar to what we do now. Anyone will be able to exit installer mode.*/
    ANYONE = "ANYONE",
    /** This setting will make sure that the only way to exit installer mode is to have the exit performed by the same user who entered installer mode or a member of the same organization.*/
    SAME_USER = "SAME_USER",
    /** This setting will only be available from the local UI and disallow exiting out of installer mode using remote means.*/
    LOCAL_ONLY = "LOCAL_ONLY",
};

export type installeModeUserInfo = {
    /** Current user is allowed to exit IM */
    isCurrentUserAllowedToExit: boolean,
    /** Info of the user who entered IM */
    userEnteredImFirstName?: string,
    userEnteredImLastName?: string,
    siteOrganizationName: string,
}
export type getInstallerModeUserInfoCb = (userUuid: string) => Promise<installeModeUserInfo>;

export enum StateNames {
    AcidEngineState = "AcidEngineState",
    BaseEngineState = "BaseEngineState",
    CO2EngineState = "CO2EngineState",
    LiquidChlorineEngineState = "LiquidChlorineEngineState",
    SaltChlorineEngineState = "SaltChlorineEngineState",
}

/** Format after deserialization of `State.state`*/
export type ChemistryEngineStateItemValue = {
    /** Explains what the engine is doing */
    ChemistryEngineState: ChemistryEngineStates,
    /** Contains a list of devices that forced us into this state.
     * This is somewhat misleadingly labeled because it’s not always true that the devices in this list have errors.
     * E.g: ChemistryEngineStates.VALUE_LESS_THAN_TARGET is a good status simply stating that we don’t need to do anything.
     * DevicesWithErrors in this case contains the probe that resulted in us deciding not to dose. */
    DevicesWithErrors: string[]
}

/**
 * @see getChemistryEngineStateLocalised()
 */
export enum ChemistryEngineStates {
    NOT_FILTERING = 'NOT_FILTERING',
    /** Not routing at a probe */
    SENSOR_MISSING_FROM_ROUTING = 'SENSOR_MISSING_FROM_ROUTING',
    /** Not routing at a chemical pump */
    DISPENSING_DEVICE_MISSING_FROM_ROUTING = 'DISPENSING_DEVICE_MISSING_FROM_ROUTING',
    WRONG_TYPE_OF_CHEMICAL_PUMP = 'WRONG_TYPE_OF_CHEMICAL_PUMP',
    CHEMICAL_PUMP_BAD_GPD = 'CHEMICAL_PUMP_BAD_GPD',
    DISPENSING_NOT_ENABLED = 'DISPENSING_NOT_ENABLED',
    WATER_MOVING_PUMP_ERROR = 'WATER_MOVING_PUMP_ERROR',
    FLOW_SWITCH_ERROR = 'FLOW_SWITCH_ERROR',
    /** Error Dispensing Acid... */
    ERROR_CONDITION = 'ERROR_CONDITION',
    /** Only Happens During Acid */
    LIQUID_CHLORINE_DOSING = 'LIQUID_CHLORINE_DOSING',
    ACID_DOSING = 'ACID_DOSING',
    PROBE_DELAY = 'PROBE_DELAY',
    NORMALIZED_VALUE_NULL = 'NORMALIZED_VALUE_NULL',
    VALUE_GREATER_THAN_TARGET = 'VALUE_GREATER_THAN_TARGET',
    VALUE_LESS_THAN_TARGET = 'VALUE_LESS_THAN_TARGET',
    SAMPLED_AT_LEAST_ONCE = 'SAMPLED_AT_LEAST_ONCE',
    SENSOR_NOT_POWERED_ON = 'SENSOR_NOT_POWERED_ON',
    SUPER_CHLORINATE = 'SUPER_CHLORINATE',
    SENSOR_UNSTABLE = 'SENSOR_UNSTABLE',
    SENSOR_OFFLINE = 'SENSOR_OFFLINE',
    ERROR_METHOD_MISSING = 'ERROR_METHOD_MISSING',
    SILENT_MAXIMUM_PER_DAY_EXCEEDED = 'SILENT_MAXIMUM_PER_DAY_EXCEEDED',
    NO_DOSING_TABLE_ROW_FOUND = 'NO_DOSING_TABLE_ROW_FOUND',
    DOSING_OR_MIXING_IN_PROGRESS = 'DOSING_OR_MIXING_IN_PROGRESS',
    NO_FLOW_WHILE_GENERATING = 'NO_FLOW_WHILE_GENERATING',
    DISPENSING_DEVICE_OFFLINE = 'DISPENSING_DEVICE_OFFLINE',
}

export enum SaltChlorinatorErrors {
    AirInLine = "AIR_IN_LINE",
    ColdWater = "COLD_WATER",
    HighSalt = "HIGH_SALT",
    InspectCell = "INSPECT_CELL",
    LowSalt = "LOW_SALT",
    NoFlowWhileGenerating = "NO_FLOW_WHILE_GENERATING",
    PowerFault = "POWER_FAULT",
    ProcessorFault = "PROCESSOR_FAULT",
    VeryLowSalt = "VERY_LOW_SALT",
}
